// Unassigned means a FRESH PIECE — nothing of the last customer left on it.
//
// Unassigning (core/admin/deviceTracking.runUnassign) takes the device away
// from its owner, location, room and every user. That left the customer's SETUP
// behind: button names, All On/All Off choices, timers, schedules, two-way
// links, its steps inside other people's scenes, the RF sensors that switch it,
// and the name they gave it. The next customer inherited all of it (asked for
// 2026-10-06: "unassigned means … make this as Fresh Piece").
//
// What a fresh piece IS was read, not guessed:
//   • OutputDetails rows are created by the backend's CheckForMissingOutputDetails
//     with FOUR values — UniqueNumber, Output, OutputName "O_<uno>_<n>",
//     DeviceInstalledId — and every other column left to the database default.
//     So the rows are DELETED and re-created the same way; the database supplies
//     the defaults, and no column is reset to a value this file made up.
//   • The name is what the firmware registers itself as: "Smartify_<uno>"
//     (OtaForSwitchingNewDesign.ino:1834, OtaForTuya.ino:6306).
//
// KEPT, because they describe the HARDWARE, not the customer: DeviceID (type),
// OTABinFileId (its firmware — a per-chip fact), the unique number itself.
//
// ⚠️ EVERY STEP IS ATTEMPTED AND REPORTED. One failed table must not stop the
// rest, and must not be swallowed either — the caller shows what was cleared
// and what was not. "Reset" that silently half-ran is the failure this whole
// codebase keeps warning about.
//
// ⚠️ THE `where` CLAUSES ARE SQL TEXT built by the backend, so the unique number
// is checked against twoWay.isSafeUniqueNumber before ANY of them is sent.

import { post, updateDeviceInstalled } from '../api/api';
import { isSafeUniqueNumber } from './twoWay';
import { invalidateNameIndex } from './nameIndex';
import { publishCommand } from '../utils/deviceController';

export const freshDeviceName = (uno) => `Smartify_${uno}`;
export const freshOutputName = (uno, output) => `O_${uno}_${output}`;

/** The registers a fresh switch holds for one output: no timers, Action/Action. */
export const freshRegisterPush = (output) => {
  const o = Number(output);
  return `swritewrite${64 + o}-0,${32 + o}-0,${48 + o}-00,${80 + o}-0`;
};

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);

// ⚠️ THIS BACKEND REPORTS MOST FAILURES AS HTTP 200 with { status: '500' } in
// the body (asynccontrollers' catch blocks use res.send, not res.status(500)),
// so a bare post() would count a failed delete as done.
const call = async (endpoint, body) => {
  const res = await post(endpoint, body);
  const d = res && res.data;
  if (d && !Array.isArray(d) && String(d.status) === '500') {
    throw new Error(`${endpoint} failed on the server${d.LineNumber ? ` (line ${d.LineNumber})` : ''}`);
  }
  return res;
};

/**
 * Reset one device to a fresh piece. Call AFTER it is unassigned.
 *
 * @param uno       unique number
 * @param device    its install row (for ID / DeviceInstalledId)
 * @param userId    optional — when given, the switch's registers are pushed too
 * -> { ok, steps: [{ key, label, ok, note? }] }
 */
export async function resetDeviceToFresh(uno, { device = null, userId = null, outputCount = 0 } = {}) {
  const u = String(uno ?? '').trim();
  if (!isSafeUniqueNumber(u)) {
    return { ok: false, steps: [{ key: 'guard', label: 'Unique number', ok: false, note: 'refused: unexpected characters' }] };
  }
  const q = `'${u}'`;
  const steps = [];
  const step = async (key, label, fn) => {
    try {
      const note = await fn();
      // Only a sentence is a note; a step that returns the server's reply has none.
      steps.push({ key, label, ok: true, ...(typeof note === 'string' && note ? { note } : {}) });
    } catch (e) {
      steps.push({ key, label, ok: false, note: String((e && e.message) || e) });
    }
  };

  // ── What the customer built AROUND the device ───────────────────────────
  await step('schedules', 'Schedules', () => call('DeleteSchedulerList', { where: `UniqueNumber = ${q}` }));
  await step('twoWay', 'Two-way links', () => call('DeleteTwoWayMapping', {
    where: `FirstUniqueNumber = ${q} OR SecondUniqueNumber = ${q}`,
  }));
  await step('scenes', 'Steps in scenes and wall buttons', () => call('DeleteWallButtonAction', {
    where: `UniqueNumber = ${q}`,
  }));
  await step('rf', 'RF sensors switching it', async () => {
    try {
      await call('DeleteRfOutputMappingsForDevice', { UniqueNumber: u });
      return null;
    } catch (e) {
      // An older server without the route answers 404 with an HTML page.
      if (e?.response?.status === 404) throw new Error('server not updated yet — deploy the backend');
      throw e;
    }
  });

  // ── The buttons themselves: delete and re-create as registration does ───
  await step('buttons', 'Button names and settings', async () => {
    const before = rowsOf(await post('GetOutputDetailbyUniqueNumber', { UniqueNumber: u }));
    const outputs = before.length
      ? [...new Set(before.map((r) => Number(r.Output)).filter((n) => n > 0))].sort((a, b) => a - b)
      : Array.from({ length: Number(outputCount) || 0 }, (_, i) => i + 1);
    const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId
      ?? before[0]?.DeviceInstalledId ?? null;
    if (!outputs.length) return 'no buttons on this device';
    await call('DeleteOutputDetails', { where: `UniqueNumber = ${q}` });
    // eslint-disable-next-line no-restricted-syntax
    for (const n of outputs) {
      // eslint-disable-next-line no-await-in-loop
      await call('AddOutputDetails', {
        UniqueNumber: u,
        Output: n,
        OutputName: freshOutputName(u, n),
        ...(installedId != null ? { DeviceInstalledId: installedId } : {}),
      });
    }
    invalidateNameIndex();
    return `${outputs.length} button${outputs.length === 1 ? '' : 's'} re-created`;
  });

  // ── The device row ──────────────────────────────────────────────────────
  await step('row', 'Device name and pending update', () => updateDeviceInstalled(u, {
    DeviceName: freshDeviceName(u),
    PendingCommand: '',
    RequireUpdateCode: 0,
  }));

  // ── The switch's own memory (best effort) ───────────────────────────────
  // Tuya firmware discards SWRITE, and an offline device misses it; the DB is
  // the record either way. Only when the caller can address the device.
  if (userId != null) {
    await step('registers', 'Switch registers', async () => {
      const rows = rowsOf(await post('GetOutputDetailbyUniqueNumber', { UniqueNumber: u }));
      rows.forEach((r) => publishCommand(u, freshRegisterPush(r.Output), userId));
      return rows.length ? 'sent (a Tuya-firmware or offline device ignores it)' : 'nothing to send';
    });
  }

  return { ok: steps.every((s) => s.ok), steps };
}
